import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Truck, Plus, X, SlidersHorizontal, RefreshCw, Search, Navigation, ChevronRight, Loader2, Pencil } from "lucide-react";
import { apiFetch } from "@/lib/api";
import { PageTitle, PageSubtitle, Loader, EmptyState, Card, BackHeader } from "@/components/common";
import { PlaceSearch } from "@/components/PlaceSearch";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";

const EMPTY = { vehicle_type: "", location: "", available_date: "", available_time: "", preferred_destination: "", payload_capacity: "", length: "", characteristics_text: "", notes: "", availability_radius_km: 50, pallet_type: "", pallets_occupied: "" };
const RADII = [5, 10, 15, 20, 30, 40, 50, 100];
const PALLETS = { italia: { label: "Italia 100×120", max: 26 }, eur: { label: "EUR/EPAL 80×120", max: 33 } };

export default function FreeVehiclesPage() {
  const { user } = useAuth();
  const isCarrier = user?.account_type === "vettore";
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [formError, setFormError] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [matches, setMatches] = useState({});
  const [expanded, setExpanded] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [f, setF] = useState({ zone: "", dest: "", vehicle: "", date: "", pickup: "", maxDistance: "", pallets: "", palletFormat: "" });

  useEffect(() => {
    const params = new URLSearchParams();
    if (f.pickup.trim()) params.set("pickup", f.pickup.trim());
    if (f.maxDistance) params.set("max_distance", f.maxDistance);
    if (f.pallets) params.set("pallets_needed", f.pallets);
    if (f.palletFormat) params.set("pallet_format", f.palletFormat);
    const qs = params.toString();
    setLoading(true);
    apiFetch(`/free-vehicles${qs ? `?${qs}` : ""}`).then(setItems).catch(() => {}).finally(() => setLoading(false));
  }, [f.pickup, f.maxDistance, f.pallets, f.palletFormat]);

  const openCreate = () => { setEditingId(null); setForm(EMPTY); setFormError(""); setShowForm(true); };
  const openEdit = (item) => {
    setEditingId(item.id);
    setForm({
      vehicle_type: item.vehicle_type || "", location: item.location || "", available_date: item.available_date || "",
      available_time: item.available_time || "", preferred_destination: item.preferred_destination || "",
      payload_capacity: item.payload_capacity || "", length: item.length || "",
      characteristics_text: (item.characteristics || []).join(", "), notes: item.notes || "",
      availability_radius_km: item.availability_radius_km ?? 50, pallet_type: item.pallet_type || "",
      pallets_occupied: item.pallets_occupied != null ? String(item.pallets_occupied) : "",
    });
    setFormError("");
    setShowForm(true);
  };

  const toggleMatches = async (id) => {
    if (expanded === id) {
      setExpanded(null);
      return;
    }
    setExpanded(id);
    if (!matches[id]) {
      const found = await apiFetch(`/free-vehicles/${id}/matches`).catch(() => []);
      setMatches((old) => ({ ...old, [id]: found }));
    }
  };

  const save = async () => {
    if (!form.vehicle_type.trim() || !form.location.trim()) {
      setFormError("Descrivi il mezzo e indica dove sarà disponibile.");
      return;
    }
    setFormError("");
    setSaving(true);
    try {
      const body = JSON.stringify({
        ...form,
        payload_capacity: form.payload_capacity || null,
        length: form.length || null,
        pallet_type: form.pallet_type || null,
        pallets_occupied: form.pallet_type ? (Number(form.pallets_occupied) || 0) : null,
        characteristics: form.characteristics_text.trim() ? form.characteristics_text.split(",").map((c) => c.trim()).filter(Boolean) : [],
      });
      let item;
      if (editingId) {
        item = await apiFetch(`/free-vehicles/${editingId}`, { method: "PUT", body });
        setItems((old) => old.map((it) => (it.id === editingId ? item : it)));
      } else {
        item = await apiFetch("/free-vehicles", { method: "POST", body });
        setItems((old) => [item, ...old]);
      }
      setShowForm(false);
      const found = await apiFetch(`/free-vehicles/${item.id}/matches`).catch(() => []);
      setMatches((old) => ({ ...old, [item.id]: found }));
      setExpanded(item.id);
      setForm(EMPTY);
      setEditingId(null);
    } catch (error) {
      setFormError(error.message || "Impossibile salvare. Riprova.");
    } finally {
      setSaving(false);
    }
  };

  const filtered = items.filter((it) => {
    if (f.zone && !(it.location || "").toLowerCase().includes(f.zone.toLowerCase())) return false;
    if (f.dest && !(it.preferred_destination || "").toLowerCase().includes(f.dest.toLowerCase())) return false;
    if (f.vehicle && !(it.vehicle_type || "").toLowerCase().includes(f.vehicle.toLowerCase())) return false;
    if (f.date && !(it.available_date || "").toLowerCase().includes(f.date.toLowerCase())) return false;
    return true;
  });

  const inputCls = "min-h-[50px] w-full rounded-xl border border-border bg-card px-3 text-[15px] outline-none focus:border-primary";
  const label = "mt-2 block text-xs font-bold text-card-foreground";

  return (
    <div className="space-y-4">
      <BackHeader />
      <div className="flex items-start justify-between">
        <div>
          <PageTitle>Camion disponibili</PageTitle>
          <PageSubtitle>{isCarrier ? "Trova un carico di ritorno per la tua prossima tratta." : "Trova un mezzo disponibile per il tuo trasporto."}</PageSubtitle>
        </div>
        {isCarrier ? (
          <button data-testid="free-vehicle-add-button" onClick={() => (showForm ? setShowForm(false) : openCreate())} className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary text-primary-foreground">
            {showForm ? <X className="h-6 w-6" /> : <Plus className="h-6 w-6" />}
          </button>
        ) : null}
      </div>

      {isCarrier && showForm ? (
        <Card className="space-y-1">
          <p className="mb-1 text-lg font-extrabold text-foreground">{editingId ? "Modifica disponibilità" : "Pubblica disponibilità"}</p>
          <label className={label}>Descrivi il mezzo</label>
          <input value={form.vehicle_type} onChange={(e) => setForm({ ...form, vehicle_type: e.target.value })} placeholder="Es. Bilico frigorifero" className={inputCls} />
          <label className={label}>Caratteristiche del mezzo</label>
          <input value={form.characteristics_text} onChange={(e) => setForm({ ...form, characteristics_text: e.target.value })} placeholder="Es. ATP, sponda idraulica" className={inputCls} />
          <div className="flex gap-2.5">
            <div className="flex-1"><label className={label}>Portata (kg)</label><input value={form.payload_capacity} onChange={(e) => setForm({ ...form, payload_capacity: e.target.value })} placeholder="Es. 24.000" className={inputCls} /></div>
            <div className="flex-1"><label className={label}>Lunghezza</label><input value={form.length} onChange={(e) => setForm({ ...form, length: e.target.value })} placeholder="Es. 13,60 m" className={inputCls} /></div>
          </div>
          <label className={label}>Tipo pedana</label>
          <div className="flex gap-2">
            {[["", "Nessuna"], ["italia", "Italia 100×120 (max 26)"], ["eur", "EUR/EPAL 80×120 (max 33)"]].map(([val, lbl]) => (
              <button key={val || "none"} type="button" data-testid={`pallet-type-${val || "none"}`} onClick={() => setForm({ ...form, pallet_type: val })} className={cn("flex-1 rounded-xl border px-2 py-2 text-[11px] font-bold", form.pallet_type === val ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card text-card-foreground")}>{lbl}</button>
            ))}
          </div>
          {form.pallet_type ? (
            <>
              <label className={label}>Pedane occupate</label>
              <input type="number" min={0} max={PALLETS[form.pallet_type].max} data-testid="pallets-occupied-input" value={form.pallets_occupied} onChange={(e) => setForm({ ...form, pallets_occupied: e.target.value })} placeholder="0" className={inputCls} />
              <p data-testid="pallets-residue-text" className="mt-1 text-[13px] font-bold text-primary">
                Capacità massima {PALLETS[form.pallet_type].max} · Occupate {Number(form.pallets_occupied) || 0} · Residue {Math.max(0, PALLETS[form.pallet_type].max - (Number(form.pallets_occupied) || 0))}
              </p>
            </>
          ) : null}
          <label className={label}>Zona attuale</label>
          <input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Es. Milano centro" className={inputCls} />
          <label className={label}>Raggio di disponibilità</label>
          <input
            type="range"
            data-testid="free-vehicle-radius-slider"
            min={0}
            max={RADII.length - 1}
            step={1}
            value={Math.max(0, RADII.indexOf(form.availability_radius_km))}
            onChange={(e) => setForm({ ...form, availability_radius_km: RADII[Number(e.target.value)] })}
            className="mt-1 w-full accent-primary"
          />
          <div className="flex justify-between text-[10px] font-bold text-muted-foreground">
            {RADII.map((r) => (<span key={r}>{r}</span>))}
          </div>
          <p data-testid="free-vehicle-radius-text" className="mt-1 text-[13px] font-bold text-primary">
            Disponibile entro {form.availability_radius_km} km da {form.location.trim() || "la località indicata"}
          </p>
          <label className={label}>Data disponibilità</label>
          <input type="date" data-testid="free-vehicle-date-input" value={form.available_date} onChange={(e) => setForm({ ...form, available_date: e.target.value })} className={inputCls} />
          <label className={label}>Ora indicativa</label>
          <input value={form.available_time} onChange={(e) => setForm({ ...form, available_time: e.target.value })} placeholder="Es. dalle 08:00" className={inputCls} />
          <label className={label}>Destinazione preferita</label>
          <input value={form.preferred_destination} onChange={(e) => setForm({ ...form, preferred_destination: e.target.value })} placeholder="Es. Campania" className={inputCls} />
          {formError ? <p data-testid="free-vehicle-error" className="mt-1 text-sm text-destructive">{formError}</p> : null}
          <button data-testid="free-vehicle-save-button" onClick={save} disabled={saving} className="mt-3 flex min-h-[52px] w-full items-center justify-center gap-2 rounded-xl bg-primary text-sm font-extrabold text-primary-foreground disabled:opacity-60">
            {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
            {saving ? "Salvataggio..." : editingId ? "SALVA MODIFICHE" : "PUBBLICA DISPONIBILITÀ"}
          </button>
        </Card>
      ) : null}

      <div className="flex items-center justify-between">
        <button data-testid="vehicles-filter-toggle" onClick={() => setShowFilters((v) => !v)} className={cn("flex items-center gap-1.5 rounded-full px-3 py-2 text-xs font-bold", showFilters ? "bg-primary text-primary-foreground" : "bg-secondary text-secondary-foreground")}>
          <SlidersHorizontal className="h-4 w-4" /> Filtri
        </button>
        <span className="text-xs text-muted-foreground">{filtered.length} {filtered.length === 1 ? "mezzo" : "mezzi"}</span>
      </div>
      {showFilters ? (
        <Card className="space-y-2.5">
          <PlaceSearch testId="vehicles-filter-pickup" value={f.pickup} onChange={(v) => setF({ ...f, pickup: v })} onSelectPlace={() => {}} placeholder="Punto di carico del viaggio (es. Monza)" />
          <select data-testid="vehicles-filter-distance" value={f.maxDistance} onChange={(e) => setF({ ...f, maxDistance: e.target.value })} className={inputCls}>
            <option value="">Distanza: qualsiasi</option>
            {RADII.map((r) => (<option key={r} value={r}>Entro {r} km</option>))}
          </select>
          <input type="number" min={1} data-testid="vehicles-filter-pallets" value={f.pallets} onChange={(e) => setF({ ...f, pallets: e.target.value })} placeholder="Pedane da caricare (completamento)" className={inputCls} />
          <select data-testid="vehicles-filter-pallet-format" value={f.palletFormat} onChange={(e) => setF({ ...f, palletFormat: e.target.value })} className={inputCls}>
            <option value="">Formato pedana: qualsiasi</option>
            <option value="italia">Italia 100×120</option>
            <option value="eur">EUR/EPAL 80×120</option>
          </select>
          <input data-testid="vehicles-filter-zone" value={f.zone} onChange={(e) => setF({ ...f, zone: e.target.value })} placeholder="Zona (dove si trova il mezzo)" className={inputCls} />
          <input data-testid="vehicles-filter-destination" value={f.dest} onChange={(e) => setF({ ...f, dest: e.target.value })} placeholder="Tratta / destinazione" className={inputCls} />
          <input data-testid="vehicles-filter-vehicle" value={f.vehicle} onChange={(e) => setF({ ...f, vehicle: e.target.value })} placeholder="Tipo di mezzo" className={inputCls} />
          <input data-testid="vehicles-filter-date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} placeholder="Disponibilità" className={inputCls} />
          <button data-testid="vehicles-filter-reset" onClick={() => setF({ zone: "", dest: "", vehicle: "", date: "", pickup: "", maxDistance: "", pallets: "", palletFormat: "" })} className="flex items-center gap-1.5 text-sm font-extrabold text-primary"><RefreshCw className="h-4 w-4" /> Azzera filtri</button>
        </Card>
      ) : null}

      {loading ? (
        <Loader />
      ) : filtered.length === 0 ? (
        <EmptyState icon={Search} title="Nessun mezzo trovato" text="Prova a modificare i filtri o la zona di ricerca." />
      ) : (
        filtered.map((item) => (
          <div key={item.id}>
            <Card className="flex items-center gap-3">
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-secondary">
                <Truck className="h-6 w-6 text-primary" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="font-extrabold text-foreground">{item.vehicle_type}</p>
                <p className="truncate text-[13px] text-muted-foreground">{item.location} · {item.available_date}{item.available_time ? ` · ore ${item.available_time}` : ""}</p>
                <p className="truncate text-[13px] text-muted-foreground">Destinazione: {item.preferred_destination || "Aperta"}</p>
                {item.characteristics?.length ? <p className="truncate text-[13px] text-muted-foreground">{item.characteristics.join(" · ")}</p> : null}
                {item.pallet_label ? (
                  <p data-testid={`vehicle-pallets-${item.id}`} className="truncate text-[13px] text-muted-foreground">
                    Pedane {item.pallet_label}: max {item.pallets_max} · occupate {item.pallets_occupied ?? 0} · <span className="font-bold text-primary">residue {item.pallets_available}</span>
                  </p>
                ) : null}
                {f.pickup.trim() ? (
                  <p data-testid={`vehicle-distance-${item.id}`} className="truncate text-[13px] font-bold text-primary">
                    {item.distance_km != null ? `Mezzo a ${item.distance_km} km dal punto di carico` : "Distanza non disponibile"}
                  </p>
                ) : null}
                <p className="truncate text-[13px] text-muted-foreground">Raggio dichiarato: {item.availability_radius_km != null ? `${item.availability_radius_km} km` : "non indicato"}</p>
                <p className="mt-1 truncate text-xs font-bold text-muted-foreground">{item.carrier_name}</p>
              </div>
              {user?.id === item.owner_id ? (
                <button data-testid={`vehicle-edit-${item.id}`} onClick={() => openEdit(item)} className="flex h-11 w-11 items-center justify-center rounded-xl bg-secondary text-primary">
                  <Pencil className="h-5 w-5" />
                </button>
              ) : null}
              <button data-testid={`vehicle-matches-${item.id}`} onClick={() => toggleMatches(item.id)} className="flex h-11 w-11 items-center justify-center rounded-xl bg-secondary text-primary">
                <Search className="h-5 w-5" />
              </button>
            </Card>
            {expanded === item.id ? (
              <Card className="mt-2 space-y-2">
                <p className="text-[13px] font-extrabold text-foreground">Carichi compatibili</p>
                {(matches[item.id] ?? []).length === 0 ? (
                  <p className="text-[13px] text-muted-foreground">Nessun viaggio compatibile al momento.</p>
                ) : (
                  (matches[item.id] ?? []).map((trip) => (
                    <Link key={trip.id} to={`/trip/${trip.id}`} data-testid={`vehicle-match-${trip.id}`} className="flex items-center gap-2 py-1.5">
                      <Navigation className="h-4 w-4 text-primary" />
                      <span className="flex-1 text-[13px] font-bold text-card-foreground">{trip.pickup} → {trip.delivery} · {trip.date}</span>
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </Link>
                  ))
                )}
              </Card>
            ) : null}
          </div>
        ))
      )}
    </div>
  );
}
